import React, { useEffect, useState, useRef, ReactNode } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { Home, Users, User, Settings, Bell, MessageSquare, Search } from "lucide-react";
import { useUser } from "../../context/UserContext";
import { useSocket } from "../../hooks/useSocket";
import { api } from "../../services/api";
import { NavItem } from "../../types";
import styles from "./Layout.module.scss";



const Layout: React.FC = () => {
  const [showBars, setShowBars] = useState(true);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const lastScroll = useRef(0);
  const location = useLocation();
  const { user } = useUser();
  const { subscribe } = useSocket();
  const navItemsFooter: NavItem[] = [
    { label: "Home", path: "/", icon: <Home size={24} /> },
    { label: "Discover", path: "/discover", icon: <Search size={24} /> },
    { label: "LFG", path: "/matchmaking", icon: <Users size={24} /> },
    { label: "Profile", path: `/profile?user=${user?.userId}`, icon: <User size={24} /> },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const currentScroll = window.scrollY;

      if (currentScroll > lastScroll.current && currentScroll > 50) {
        setShowBars(false); // scrolling down
      } else if (currentScroll < lastScroll.current) {
        setShowBars(true);  // scrolling up
      }

      lastScroll.current = currentScroll;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Fetch unread notification count on mount
  useEffect(() => {
    if (!user) return;

    const fetchNotificationCount = async () => {
      try {
        const res = await api.notifications.getUnreadCount();
        setUnreadNotifications(res.count);
      } catch (err) {
        // Silently fail — badge will show 0
      }
    };

    fetchNotificationCount();
  }, [user]);

  // Fetch unread message count on mount
  useEffect(() => {
    if (!user) return;

    const fetchMessageCount = async () => {
      try {
        const res = await api.messaging.listConversations();
        const total = (res.items || []).reduce(
          (sum: number, conv: any) => sum + (conv.unreadCount || 0),
          0
        );
        setUnreadMessages(total);
      } catch (err) {
        // Silently fail — badge will show 0
      }
    };

    fetchMessageCount();
  }, [user]);

  // Subscribe to real-time WebSocket events for badge updates
  useEffect(() => {
    if (!user) return;

    const unsubNotification = subscribe("notification", () => {
      // Don't increment if user is already on the notifications page
      if (location.pathname !== "/notifications") {
        setUnreadNotifications((prev) => prev + 1);
      }
    });

    const unsubMessage = subscribe("new_message", (msg: any) => {
      // Ignore messages sent by the current user
      if (msg.senderId === user?.userId) return;
      // Don't increment if user is already on the messages page
      if (location.pathname !== "/messages") {
        setUnreadMessages((prev) => prev + 1);
      }
    });

    return () => {
      unsubNotification();
      unsubMessage();
    };
  }, [user, subscribe, location.pathname]);

  // Clear notification badge when visiting notifications page
  useEffect(() => {
    if (location.pathname === "/notifications") {
      setUnreadNotifications(0);
    }
  }, [location.pathname]);

  // Clear message badge when visiting messages page
  useEffect(() => {
    if (location.pathname === "/messages") {
      setUnreadMessages(0);
    }
  }, [location.pathname]);

  return (
    <div className={styles.layout}>
      {/* Top Toolbar */}
      <header
        className={`${styles.topBar} ${showBars ? styles.visible : styles.hidden
          }`}
      >
        <div className={styles.left}></div>

        <div className={styles.center}>
          <p className={styles.cursive}>Hippocampus Gaming</p>
        </div>

        <div className={styles.right}>
          <Link
            to="/notifications"
            className={`${styles.navIcon} ${location.pathname === "/notifications" ? styles.active : ""
              }`}
          >
            <Bell size={22} />
            {unreadNotifications > 0 && (
              <span className={styles.badge}>
                {unreadNotifications > 99 ? "99+" : unreadNotifications}
              </span>
            )}
          </Link>
          <Link
            to="/messages"
            className={`${styles.navIcon} ${location.pathname === "/messages" ? styles.active : ""
              }`}
          >
            <MessageSquare size={22} />
            {unreadMessages > 0 && (
              <span className={styles.badge}>
                {unreadMessages > 99 ? "99+" : unreadMessages}
              </span>
            )}
          </Link>
          <Link
            to="/settings"
            className={`${styles.navIcon} ${location.pathname === "/settings" ? styles.active : ""
              }`}
          >
            <Settings size={22} />
          </Link>
        </div>
      </header>

      {/* Main Page Content */}
      <main className={`${styles.mainContent} centerPage`}>
        <Outlet />
      </main>

      {/* Bottom Toolbar */}
      <nav
        className={`${styles.bottomBar} ${showBars ? styles.visible : styles.hidden
          }`}
      >
        {navItemsFooter.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`${styles.navItem} ${location.pathname === item.path ? styles.active : ""
              }`}
            onClick={(e) => {
              if (item.path === "/" && location.pathname === "/") {
                e.preventDefault(); // stop re-navigating
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
          >
            {item.icon}
            <span>{item.label}</span>
          </Link>
        ))}
      </nav>
    </div>
  );
};

export default Layout;
